import { DISPOSITIONS, DISPOSITION_HELP, TREATMENTS, TREATMENT_HELP } from "@/lib/dispositionConstants";

/**
 * 6×6 grid: technical treatment (rows) × migration disposition (columns).
 * Cells read "validated / total"; click filters the table to that cell.
 */
export default function DispositionMatrixGrid({ grid, selected, onSelect }) {
  const isSel = (t, d) => selected?.treatment === t && selected?.disposition === d;
  return (
    <div className="ml-card overflow-x-auto" data-testid="disposition-matrix-grid">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[var(--ml-border)] bg-[var(--ml-surface)]">
            <th className="px-3 py-2 text-left ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
              treatment \ disposition
            </th>
            {DISPOSITIONS.map((d) => (
              <th key={d} title={DISPOSITION_HELP[d]} className="px-3 py-2 text-center ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
                {d}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {TREATMENTS.map((t) => (
            <tr key={t} className="border-b border-[var(--ml-border)] last:border-b-0">
              <th title={TREATMENT_HELP[t]} className="px-3 py-2 text-left ml-mono text-xs font-semibold bg-[var(--ml-surface)]/60">
                {t}
              </th>
              {DISPOSITIONS.map((d) => {
                const cell = grid?.[t]?.[d] || { total: 0, validated: 0, stale: 0 };
                const empty = !cell.total;
                const staleN = cell.stale || 0;
                return (
                  <td key={d} className="px-1 py-1 text-center">
                    <button
                      type="button"
                      disabled={empty}
                      onClick={() => onSelect(isSel(t, d) ? null : { treatment: t, disposition: d })}
                      data-testid={`matrix-cell-${t}-${d}`}
                      data-stale={staleN}
                      className={`w-full px-2 py-1.5 text-xs tabular-nums border transition-colors ${
                        empty
                          ? "border-transparent text-[var(--ml-border)] cursor-default"
                          : isSel(t, d)
                            ? "border-[var(--ml-primary)] bg-[var(--ml-primary)]/10 text-[var(--ml-primary)]"
                            : "border-[var(--ml-border)] hover:border-[var(--ml-primary)]/60"
                      }`}
                      title={empty ? "" : `${cell.validated} validated of ${cell.total}${staleN ? ` · ${staleN} stale (signal no longer fires)` : ""}`}
                    >
                      {empty ? "·" : (
                        <>
                          <span className={cell.validated ? "font-semibold text-[#0e6027]" : ""}>{cell.validated}</span>
                          <span className="text-[var(--ml-text-secondary)]"> / {cell.total}</span>
                          {staleN > 0 && (
                            <span className="ml-1 inline-block w-1.5 h-1.5 rounded-full bg-[#ff832b] align-middle" data-testid={`matrix-stale-${t}-${d}`} />
                          )}
                        </>
                      )}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="px-3 py-2 text-[11px] text-[var(--ml-text-secondary)] border-t border-[var(--ml-border)]">
        Cells show <span className="text-[#0e6027] font-semibold">validated</span> / total. Treatment is what happens to the code or data
        structure; disposition is where the object ends up — kept apart on purpose.
        <span className="ml-1 inline-block w-1.5 h-1.5 rounded-full bg-[#ff832b] align-middle" /> marks cells holding stale proposals (driving signal no longer fires).
      </div>
    </div>
  );
}
